import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { IconComponent } from '@libs/components/icons';
import { CapabilityService } from '@libs/shared/services';
import { CHECKOUT_CART_ROUTE_NAME, NavItemModel } from '@libs/shared/models';
import { CartService, NavigationService } from '@libs/shared/services';
import { NavLinkDirective } from '@libs/shared/ui';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'ui-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatBadgeModule,
    MatButtonModule,
    MatRippleModule,
    MatBottomSheetModule,
    NgTemplateOutlet,
    NavLinkDirective,
    IconComponent,
  ],
})
export class NavbarComponent {
  public items: Signal<NavItemModel[]> = computed(() => {
    const children: readonly NavItemModel[] = this.navbar()?.children as NavItemModel[] ?? [];

    if (!children.length) {
      return [];
    }

    const items: NavItemModel[] = [...children];

    const cartItemIndex: number = items.findIndex(item => item.name === CHECKOUT_CART_ROUTE_NAME);

    if (cartItemIndex !== -1) {
      items[cartItemIndex] = {
        ...items[cartItemIndex],
        badgeCount: this.cartCount(),
      };
    }

    return items;
  });

  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly cartService: CartService = inject(CartService);
  private readonly router: Router = inject(Router);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);

  private readonly navbar: Signal<NavItemModel | undefined> = toSignal(this.navigationService.getNavbarNavigation());
  private readonly cartCount: Signal<number | undefined> = toSignal(
    this.capabilityService.isAvailable('shop')() ? this.cartService.getCartItemsCount() : EMPTY,
  );

  public isItemActive(item: NavItemModel): boolean {
    if (!item.url) {
      return false;
    }

    return this.router.isActive(item.url, {
      matrixParams: 'subset',
      queryParams: 'ignored',
      paths: 'subset',
      fragment: 'exact',
    });
  }
}
